/**
 * Advanced mode — the visitor's blueprints, in localStorage.
 *
 * A blueprint is one floor plan (one room, unless the admin allows more) with
 * the room the 3D view was showing. The visitor keeps several and works on
 * one at a time (`activeId`): the 3D view only ever shows the active one.
 * Separate from the sandbox's store (sandboxStorage.ts): the sandbox is for
 * developing the designer, this is a visitor's work.
 *
 * Anything unreadable or not shaped like a plan is dropped; a plan with more
 * rooms than the admin now allows keeps its first rooms only.
 */

import { FloorPlan, deleteRooms } from '../../../calculations/plan';
import { isPlan } from '../designerDocument';
import { DesignerUiState, uiOf } from '../sandboxStorage';

const KEY = 'designer.advanced.v1';
const VERSION = 1;

export interface AdvancedBlueprint {
  id: string;
  /** The name shown for a blueprint with no room yet; a room's own name once it has one (blueprintName). */
  name: string;
  plan: FloorPlan;
  /** The room the 3D view shows. */
  focusRoomId: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface AdvancedState {
  blueprints: AdvancedBlueprint[];
  activeId: string;
  plannerOpen: boolean;
  ui: DesignerUiState;
}

export interface ParseOptions {
  /** The most rooms a blueprint may hold now; extra rooms are dropped. */
  maxRooms?: number;
}

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

export function newBlueprintId(): string {
  return `bp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function blankBlueprint(name: string): AdvancedBlueprint {
  const now = Date.now();
  return { id: newBlueprintId(), name, plan: { rooms: [], openWalls: [] }, focusRoomId: null, createdAt: now, updatedAt: now };
}

/** What a blueprint is called: its room's name, or its own while it has none. */
export function blueprintName(bp: Pick<AdvancedBlueprint, 'name' | 'plan'>): string {
  return bp.plan.rooms[0]?.name ?? bp.name;
}

/** One blank blueprint, the planner open. */
export function freshAdvancedState(): AdvancedState {
  const bp = blankBlueprint('Room 1');
  return { blueprints: [bp], activeId: bp.id, plannerOpen: true, ui: { lockedFace: null, selectedSectionId: null } };
}

/** A plan with no more than `maxRooms` rooms: the first ones stay. */
export function clampRooms(plan: FloorPlan, maxRooms: number | undefined): FloorPlan {
  if (maxRooms === undefined || plan.rooms.length <= maxRooms) return plan;
  const extra = plan.rooms.slice(maxRooms).map((r) => r.id);
  const trimmed = deleteRooms(plan, extra);
  return trimmed.ok ? trimmed.plan : plan;
}

function parseBlueprint(data: unknown, opts: ParseOptions): AdvancedBlueprint | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  if (typeof d.id !== 'string' || d.id === '' || !isPlan(d.plan)) return null;
  const plan = clampRooms(d.plan, opts.maxRooms);
  const focusRoomId = plan.rooms.some((r) => r.id === d.focusRoomId) ? (d.focusRoomId as string) : plan.rooms[0]?.id ?? null;
  const now = Date.now();
  return {
    id: d.id,
    name: typeof d.name === 'string' && d.name.trim() ? d.name : 'Room',
    plan,
    focusRoomId,
    createdAt: isNum(d.createdAt) ? d.createdAt : now,
    updatedAt: isNum(d.updatedAt) ? d.updatedAt : now,
  };
}

/** Parse a stored payload. Exported for tests; returns null for anything unusable. */
export function parseAdvancedState(raw: string | null, opts: ParseOptions = {}): AdvancedState | null {
  if (!raw) return null;
  try {
    const data = JSON.parse(raw);
    if (!data || data.v !== VERSION || !Array.isArray(data.blueprints)) return null;
    const seen = new Set<string>();
    const blueprints: AdvancedBlueprint[] = [];
    for (const item of data.blueprints) {
      const bp = parseBlueprint(item, opts);
      if (!bp || seen.has(bp.id)) continue;
      seen.add(bp.id);
      blueprints.push(bp);
    }
    if (blueprints.length === 0) return null;
    const activeId = blueprints.some((b) => b.id === data.activeId) ? data.activeId : blueprints[0].id;
    return { blueprints, activeId, plannerOpen: data.plannerOpen !== false, ui: uiOf(data) };
  } catch {
    return null;
  }
}

export function loadAdvancedState(opts: ParseOptions = {}): AdvancedState | null {
  try {
    return parseAdvancedState(window.localStorage.getItem(KEY), opts);
  } catch {
    return null; // storage blocked
  }
}

export function saveAdvancedState(state: AdvancedState): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify({ v: VERSION, ...state }));
  } catch {
    // storage blocked or full — the designer still works, it just won't remember
  }
}

export function clearAdvancedState(): void {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}

/** Whether any blueprint has a room: a design worth offering back. */
export function hasSavedDesign(state: AdvancedState): boolean {
  return state.blueprints.some((b) => b.plan.rooms.length > 0);
}

/**
 * Router state for opening `/design` on the kept design without the
 * continue-or-start-new prompt: Back to Edit, a template, a reload once answered.
 */
export const OPEN_SAVED_DESIGN = { resume: 'continue' } as const;

/** Whether arriving with this router state should ask before reopening the kept design. */
export function asksToResume(routerState: unknown, state: AdvancedState): boolean {
  const skip = !!routerState && typeof routerState === 'object' && (routerState as { resume?: unknown }).resume === OPEN_SAVED_DESIGN.resume;
  return !skip && hasSavedDesign(state);
}

/**
 * One blueprint holding the room of an estimate, the 3D view open: what
 * "Back to Edit" restores after a save has cleared the visitor's blueprints
 * (a saved estimate ends the Advanced session; the next one starts blank).
 */
export function seedAdvancedState(doc: { plan: FloorPlan; focusRoomId: string | null }): AdvancedState {
  const now = Date.now();
  const bp: AdvancedBlueprint = {
    id: newBlueprintId(), name: doc.plan.rooms[0]?.name ?? 'Room 1', plan: doc.plan,
    focusRoomId: doc.plan.rooms.some((r) => r.id === doc.focusRoomId) ? doc.focusRoomId : doc.plan.rooms[0]?.id ?? null,
    createdAt: now, updatedAt: now,
  };
  const state: AdvancedState = { blueprints: [bp], activeId: bp.id, plannerOpen: bp.plan.rooms.length === 0, ui: { lockedFace: null, selectedSectionId: null } };
  saveAdvancedState(state);
  return state;
}
